/**
 * Styles for the app shell (rail, header bar, menus). Rendered once in a
 * <style> tag by AppRail. Tokens only: colours, type and radii come from
 * the host page / shared/tokens, so the shell follows light and dark.
 */
export const SHELL_CSS = `
.app-rail-nav {
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
  padding: var(--space-2);
  gap: 2px;
}
.shell-brand {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: 10px var(--space-3) 12px;
  font-family: var(--font-ui);
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--text);
}
.shell-brand-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--accent);
  box-shadow: 0 0 0 4px var(--accent-glow);
  flex-shrink: 0;
}
.app-rail-items {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-height: 0;
  margin-top: var(--space-2);
}
.app-rail-item {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  border: none;
  border-left: 2px solid transparent;
  background: transparent;
  color: var(--text-dim);
  font-family: var(--font-ui);
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  cursor: pointer;
  border-radius: var(--radius-sm);
  text-align: left;
  width: 100%;
  transition: background 0.1s ease-out, color 0.1s ease-out;
}
.app-rail-item-icon { display: inline-flex; flex-shrink: 0; align-items: center; justify-content: center; }
.app-rail-item-label { flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.app-rail-item:hover { background: var(--bg3); color: var(--text); }
.app-rail-item.active {
  background: var(--accent-glow);
  color: var(--text);
  border-left-color: var(--accent);
  font-weight: 600;
}
.app-rail-item.active .app-rail-item-icon { color: var(--accent); }
.app-rail-item:focus-visible,
.app-rail-toggle:focus-visible,
.shell-profile-btn:focus-visible,
.shell-new-btn:focus-visible,
.shell-search:focus-visible,
.shell-view:focus-visible,
.shell-menu-item:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
}
.shell-rail-foot {
  display: flex;
  flex-direction: column;
  gap: 2px;
  border-top: 1px solid var(--border);
  padding-top: var(--space-2);
}
.app-rail-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-2);
  border: none;
  background: transparent;
  color: var(--text-dim);
  cursor: pointer;
  border-radius: var(--radius-sm);
}
.app-rail-toggle:hover { background: var(--bg3); color: var(--text); }
.shell-profile-btn {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  width: 100%;
  padding: 6px var(--space-2);
  border: none;
  background: transparent;
  color: var(--text-dim);
  font-family: var(--font-ui);
  font-size: 13px;
  cursor: pointer;
  border-radius: var(--radius-sm);
  text-align: left;
}
.shell-profile-btn:hover { background: var(--bg3); color: var(--text); }
.shell-avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--bg3);
  border: 1px solid var(--border2);
  color: var(--text);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}
.shell-profile-email { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
body.rail-collapsed .app-rail-item,
body.rail-collapsed .shell-profile-btn { justify-content: center; padding-left: var(--space-2); padding-right: var(--space-2); }
body.rail-collapsed .app-rail-item-label,
body.rail-collapsed .shell-profile-email,
body.rail-collapsed .shell-brand-name { display: none; }
body.rail-collapsed .shell-brand { justify-content: center; padding-left: 0; padding-right: 0; }

/* Header: screen title + view switch */
.shell-bar { display: flex; align-items: center; gap: var(--space-4); min-width: 0; }
.shell-title {
  margin: 0;
  font-family: var(--font-ui);
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.015em;
  color: var(--text);
  white-space: nowrap;
}
.shell-views {
  display: inline-flex;
  padding: 3px;
  gap: 2px;
  background: var(--bg3);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  min-width: 0;
  overflow-x: auto;
  scrollbar-width: none;
}
.shell-views::-webkit-scrollbar { display: none; }
.shell-view {
  border: none;
  background: transparent;
  color: var(--text-dim);
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 500;
  padding: 5px 12px;
  border-radius: var(--radius-sm);
  cursor: pointer;
  white-space: nowrap;
}
.shell-view:hover { color: var(--text); }
.shell-view[aria-selected='true'] { background: var(--bg2); color: var(--text); box-shadow: var(--shadow-sm); }

/* Header: actions */
.shell-actions { display: flex; align-items: center; gap: var(--space-2); }
.shell-search {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  height: 34px;
  padding: 0 10px;
  border: 1px solid var(--border);
  background: var(--bg);
  color: var(--text-muted);
  font-family: var(--font-ui);
  font-size: 13px;
  border-radius: var(--radius);
  cursor: pointer;
  min-width: 180px;
}
.shell-search:hover { border-color: var(--border2); color: var(--text-dim); }
.shell-search kbd {
  margin-left: auto;
  font-family: var(--font-mono);
  font-size: 11px;
  padding: 1px 6px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  background: var(--bg3);
  color: var(--text-dim);
}
.shell-new-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px 0 10px;
  border: none;
  background: var(--accent);
  color: var(--on-accent);
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 600;
  border-radius: var(--radius);
  cursor: pointer;
}
.shell-new-btn:hover { box-shadow: var(--shadow-1); }
.shell-avatar-top { display: none; }
.shell-avatar-top .shell-profile-btn { padding: 2px; width: auto; }

/* Menus */
.shell-menu-root { position: relative; }
.shell-menu {
  position: fixed;
  z-index: 1200;
  min-width: 220px;
  max-width: calc(100vw - 16px);
  background: var(--bg2);
  border: 1px solid var(--border2);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  padding: 6px;
}
.shell-menu-section + .shell-menu-section { border-top: 1px solid var(--border); margin-top: 6px; padding-top: 6px; }
.shell-menu-heading {
  padding: 6px 10px 4px;
  font-size: 12px;
  color: var(--text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.shell-menu-item {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  width: 100%;
  padding: 8px 10px;
  border: none;
  background: transparent;
  color: var(--text);
  font-family: var(--font-ui);
  font-size: 13px;
  text-align: left;
  border-radius: var(--radius-sm);
  cursor: pointer;
}
.shell-menu-item:hover, .shell-menu-item:focus-visible { background: var(--bg3); }
.shell-menu-label { flex: 1; }
.shell-menu-hint { font-family: var(--font-mono); font-size: 11px; color: var(--text-muted); }
.shell-switch {
  width: 28px; height: 16px; border-radius: 999px; background: var(--border2); position: relative; flex-shrink: 0;
  transition: background 0.15s ease-out;
}
.shell-switch::after {
  content: ''; position: absolute; top: 2px; left: 2px; width: 12px; height: 12px; border-radius: 50%;
  background: var(--bg2); transition: transform 0.15s ease-out;
}
.shell-switch.on { background: var(--accent); }
.shell-switch.on::after { transform: translateX(12px); }

/* Phones: the rail becomes a bottom bar with the five screens. */
@media (max-width: 639px) {
  .app-rail-nav { flex-direction: row; padding: 4px 4px env(safe-area-inset-bottom); }
  .shell-brand, .shell-rail-foot, .app-rail-nav .biz-switcher { display: none; }
  .app-rail-items { flex-direction: row; margin: 0; gap: 0; }
  .app-rail-item,
  body.rail-collapsed .app-rail-item {
    flex: 1;
    flex-direction: column;
    justify-content: center;
    gap: 2px;
    padding: 6px 2px;
    border-left: none;
    border-top: 2px solid transparent;
    border-radius: 0;
    font-size: 11px;
    line-height: 14px;
    text-align: center;
  }
  .app-rail-item.active { background: transparent; border-top-color: var(--accent); }
  .app-rail-item-label, body.rail-collapsed .app-rail-item-label { display: block; flex: none; }
  .shell-title { font-size: 16px; }
  /* The view switch names the screen; drop the title to fit a phone. */
  .shell-bar.has-views .shell-title { display: none; }
  .shell-bar { gap: var(--space-2); }
  .shell-search { min-width: 0; width: 34px; padding: 0; justify-content: center; }
  .shell-search span, .shell-search kbd { display: none; }
  .shell-new-btn { width: 34px; padding: 0; justify-content: center; }
  .shell-new-btn span { display: none; }
  .shell-avatar-top { display: block; }
}
@media (max-width: 380px) {
  .shell-views .shell-view { padding: 5px 8px; }
}
`
